import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Meter, Panel } from '../common/Panel';

type Summary = RouterOutputs['period']['summary'];
type Envelope = Summary['envelopes']['items'][number];

const TONE = {
  under: 'positive',
  // biome-ignore lint/style/useNamingConvention: mirrors the API's status value
  on_track: 'positive',
  over: 'warning',
  exceeded: 'negative',
} as const;

/** Optional monthly limits ("Mat 7 000 kr") with where pace says you should be. */
export function LimitsCard({ summary }: { summary: Summary }) {
  const limits = summary.envelopes.items.filter((e) => e.budgeted > 0);
  const { period } = summary;
  const timeShare = period.daysTotal > 0 ? period.daysElapsed / period.daysTotal : 1;

  if (limits.length === 0) {
    return (
      <Panel title="Gränser">
        <p className="text-sm text-muted-foreground">
          Vill du hålla koll på t.ex. mat eller restaurang? Sätt en gräns per månad så ser du här
          hur det går.
        </p>
        <Link
          to="/plan"
          className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
        >
          Lägg till en gräns
        </Link>
      </Panel>
    );
  }

  return (
    <Panel
      title="Gränser"
      action={
        <Link to="/plan" className="font-medium text-primary hover:underline">
          Ändra
        </Link>
      }
    >
      <ul className="space-y-4">
        {limits.map((limit) => (
          <LimitRow
            key={limit.budgetId}
            limit={limit}
            marker={period.isCurrent ? timeShare : undefined}
          />
        ))}
      </ul>
    </Panel>
  );
}

function LimitRow({ limit, marker }: { limit: Envelope; marker: number | undefined }) {
  const tone = TONE[limit.status];
  const over = limit.remaining < 0;
  return (
    <li>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="truncate font-medium">{limit.name}</span>
        <span
          className={cn(
            'shrink-0 text-sm tabular-nums',
            over ? 'text-negative' : 'text-muted-foreground',
          )}
        >
          {over ? `${kr(-limit.remaining)} över` : `${kr(limit.remaining)} kvar`}
        </span>
      </div>
      <Meter
        value={limit.spent / limit.budgeted}
        marker={marker}
        tone={tone}
        label={`${limit.name}: ${kr(limit.spent)} av ${kr(limit.budgeted)}`}
      />
      <p className="mt-1 text-xs text-muted-foreground tabular-nums">
        {kr(limit.spent)} av {kr(limit.budgeted)}
      </p>
    </li>
  );
}
